import type { ReactNode } from 'react';
import { T, AppHeader } from '../../design';
import { usePortal } from '../../context/PortalContext';
import type { Screen, NavStack } from '../../portal/navigation';
import type { SalonEmployeeRow, MeStats, SalonStats, ChainStats } from '../../types/primeStats.types';
import { getMyStats, getSalonStats, getSalonDrilldown, getChainStats } from '../../services/primeStatsService';
import { usePrimeStatsData } from '../../hooks/primeStats';
import { MeView, SalonView, EmployeeDetail, ChainView } from './screens/views';
import { StatsSkeleton, StatsError } from './screens/states';

export interface PrimeStatsModuleProps {
  screen: Screen;
  nav: NavStack;
}

const MAIN_PAD = '16px 14px calc(40px + env(safe-area-inset-bottom))';

type RootView = 'me' | 'salon' | 'chain';

export function PrimeStatsModule({ screen, nav }: PrimeStatsModuleProps) {
  const portal = usePortal();
  const employeeId = portal.employee?.id ?? null;

  const rootView: RootView = portal.isAdminMode ? 'chain' : portal.isOwner ? 'salon' : 'me';
  const empScreen = screen.type === 'prime-employee';
  const salonScreen = screen.type === 'prime-salon';
  const drillSalonId = typeof screen.salonId === 'number' ? screen.salonId : null;

  let loader: (() => Promise<MeStats | SalonStats | ChainStats>) | null = null;
  let depKey = `${screen.type}:${rootView}`;
  if (empScreen) {
    loader = null;
  } else if (salonScreen && drillSalonId != null) {
    loader = () => getSalonDrilldown(drillSalonId);
    depKey = `salon:${drillSalonId}`;
  } else if (rootView === 'chain') {
    loader = () => getChainStats();
  } else if (rootView === 'salon') {
    loader = employeeId != null ? () => getSalonStats() : null;
  } else {
    loader = employeeId != null ? () => getMyStats() : null;
  }

  const { loading, error, data, refresh } = usePrimeStatsData(loader, depKey);

  function openEmployee(row: SalonEmployeeRow, salonName: string, salonTotal: number) {
    nav.push({ type: 'prime-employee', employeeId: row.employeeId, row, salonName, salonTotal });
  }

  let title = 'PRIME';
  let body: ReactNode = null;

  if (empScreen) {
    const row = screen.row as SalonEmployeeRow;
    const salonName = (screen.salonName as string) ?? '';
    const salonTotal = typeof screen.salonTotal === 'number' ? screen.salonTotal : Math.max(1, row.total);
    title = row.name;
    body = <EmployeeDetail row={row} salonName={salonName} salonTotal={salonTotal} />;
  } else if (loading) {
    title = rootView === 'chain' ? 'Hele kæden' : rootView === 'salon' ? (portal.salon?.name ?? 'Salon') : 'Min PRIME';
    body = <StatsSkeleton />;
  } else if (error || !data) {
    title = 'PRIME Statistik';
    body = <StatsError onRetry={refresh} message={error ?? undefined} />;
  } else if (salonScreen && drillSalonId != null) {
    const s = data as SalonStats;
    title = s.salon.name;
    body = <SalonView data={s} onEmployee={(row) => openEmployee(row, s.salon.name, s.totals.total)} />;
  } else if (rootView === 'chain') {
    title = 'Hele kæden';
    body = <ChainView data={data as ChainStats} onSalon={(salonId) => nav.push({ type: 'prime-salon', salonId })} />;
  } else if (rootView === 'salon') {
    const s = data as SalonStats;
    title = s.salon.name;
    body = <SalonView data={s} onEmployee={(row) => openEmployee(row, s.salon.name, s.totals.total)} />;
  } else {
    title = 'Min PRIME';
    body = <MeView data={data as MeStats} />;
  }

  return (
    <>
      <AppHeader eyebrow="PRIME Statistik" title={title} onBack={nav.back} />
      <main style={{ flex: 1, padding: MAIN_PAD, background: T.bg }}>{body}</main>
    </>
  );
}
